// src/app/store/useUserStore.ts
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { UserRole } from '../../types/user';
import type { DefaultRole } from '../../api/userApi2';
import type { UserProfileRole } from '../../api/userApi3';

export interface WeightUnitOption {
  code: string; // e.g. 'lb', 'kg'
  multiplierFromLb: number;
}

export interface Address {
  country_name: string;
  state_name: string;
  city_name: string;
  postal_code: string;
  street_no_one: string;
  street_no_two: string;
  longitude: string;
  latitude: string;
}

// Re-export UserRole for backward compatibility
export { UserRole };

export interface User {
  user_id?: number | string; // Can be encrypted string or number
  negotiateID?: number | string; // Numeric ID for API calls
  uuid?: string; // UUID from backend
  auth0_id?: string;
  full_name?: string;
  birthday?: string;
  phone_number?: string | null;
  user_role?: UserRole;
  address?: Address;
  profile_image?: string;
  email?: string;
  name?: string;
  picture?: string;
  sub?: string;
  kyc_status?: string; // KYC verification status (verified = true)
  card_verified?: boolean; // Card verification status
  isSocialLogin?: boolean; // Whether user authenticated via social login (cannot change password in-app)
  roles?: UserProfileRole[]; // All roles assigned to this user
  has_admin_portal_access?: boolean; // Whether user has access to the admin portal
  data_policy_accepted?: boolean;
  first_login?: boolean;
}

interface UserStore {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isProfileLoading: boolean;
  defaultRoles: DefaultRole[];
  weightUnits: WeightUnitOption[];

  // Actions
  setUser: (user: User) => void;
  updateUserProfile: (profile: Partial<User>) => void;
  updateUserRole: (role: UserRole) => void;
  setDefaultRoles: (roles: DefaultRole[]) => void;
  setWeightUnits: (units: WeightUnitOption[]) => void;
  logout: () => void;
  setProfileLoading: (loading: boolean) => void;

  // Computed/Helper methods
  isProfileComplete: () => boolean;
  isFullyAuthenticated: () => boolean;
  hasBasicProfile: () => boolean;
  isKycVerified: (overrideKycStatus?: string, overrideCardVerified?: boolean) => boolean;
}

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial State
        user: null,
        isAuthenticated: false,
        isProfileLoading: true,
        defaultRoles: [],
        weightUnits: [],

        // Actions
        setUser: (user: User) => {
          set(() => ({
            user: {
              ...user,
              negotiateID: user.negotiateID || (typeof user.user_id === 'number' ? user.user_id : undefined),
            },
            isAuthenticated: true,
            isProfileLoading: false,
          }), false, 'setUser');
        },

        updateUserProfile: (profile: Partial<User>) => {
          set((state) => ({
            user: state.user ? { ...state.user, ...profile } : null,
          }), false, 'updateUserProfile');
        },

        updateUserRole: (role: UserRole) => {
          set((state) => ({
            user: state.user ? { ...state.user, user_role: role } : null,
          }), false, 'updateUserRole');
        },

        setDefaultRoles: (roles: DefaultRole[]) => {
          set({ defaultRoles: roles }, false, 'setDefaultRoles');
        },

        setWeightUnits: (units: WeightUnitOption[]) => {
          set({ weightUnits: units }, false, 'setWeightUnits');
        },

        logout: () => {
          set({
            user: null,
            isAuthenticated: false,
            isProfileLoading: false,
          }, false, 'logout');
        },

        setProfileLoading: (loading: boolean) => {
          set({ isProfileLoading: loading }, false, 'setProfileLoading');
        },

        // Helper Methods
        isProfileComplete: () => {
          const { user } = get();
          if (!user) return false;

          // Check for required fields: first name, last name, email, and home address
          const fullName = user?.full_name?.trim();
          if (!fullName) return false;

          const nameParts = fullName.split(' ').filter(part => part.length > 0);
          const hasFullName = nameParts.length >= 2;

          return !!(
            hasFullName &&
            user?.email &&
            user?.address?.street_no_one
          );
        },

        isFullyAuthenticated: () => {
          const { isAuthenticated, user } = get();
          return !!(
            isAuthenticated &&
            user?.user_id &&
            user?.auth0_id
          );
        },

        // Check if basic profile info (name) is set
        hasBasicProfile: () => {
          const { user } = get();
          if (!user) return false;

          // Check if full_name exists and contains both first and last name
          const fullName = user?.full_name?.trim();
          if (!fullName) return false;

          // Check if name has at least 2 parts (first name and last name)
          const nameParts = fullName.split(' ').filter(part => part.length > 0);
          return nameParts.length >= 2;
        },

        isKycVerified: (overrideKycStatus?: string, overrideCardVerified?: boolean) => {
          const { user } = get();
          if (!user) return false;
          const kycStatus = overrideKycStatus !== undefined ? 'verified' : user.kyc_status;
          const cardVerified = overrideCardVerified !== undefined ? overrideCardVerified : user.card_verified;
          return kycStatus === 'verified' && !!cardVerified;
        },
      }),
      {
        name: 'skyporter-user-store',
        // Nothing sensitive to persist
        partialize: () => ({}),
      }
    ),
    { name: 'UserStore' }
  )
);
